/**
 * The ⌘K palette's **documents** source.
 *
 * `chrome-oem/OemCommandPalette.tsx` takes pluggable sources and ships only
 * navigation; P3 and P4 registered machines, work orders, parts and people.
 * This is the lane that owns the lake, so this is where its 271 files are
 * registered.
 *
 * ## Why the source is warmed rather than awaited
 *
 * `PaletteSource.search` must be **synchronous** — it runs on every keystroke.
 * The lake catalogue is a 131 KB fetch. So this module kicks that fetch off
 * once, the first time anything asks, keeps the resolved rows in module state,
 * and re-registers the source when they land so an open palette re-runs its
 * query. Until then the source returns nothing, which costs a user who opens
 * ⌘K in the first few hundred milliseconds one keystroke, and costs everyone
 * else nothing on first paint.
 *
 * Registering at module scope means importing this file is what turns the
 * source on. `DataBrowserScene.tsx` imports it; the router imports that scene
 * eagerly, so the source is live for the whole session regardless of which
 * page the user lands on.
 *
 * @see src/versions/v1/chrome-oem/OemCommandPalette.tsx — `registerPaletteSource`
 * @see src/versions/v1/modules/integration/DataBrowserScene.tsx
 */

import { FileText } from 'lucide-react';
import {
  registerPaletteSource,
  type PaletteResult,
} from '../../chrome-oem/OemCommandPalette';
import type { LakeEntry } from '../../schema/document';
import { loadLakeIndex } from '../../shared/useOemData';

/** Resolved rows, or an empty list until the catalogue lands. */
let rows: PaletteResult[] = [];
let started = false;

/** Search text per row, precomputed — `search` runs on every keystroke. */
let haystack: string[] = [];

/**
 * The palette lays a row out as label on the left and hint on the right, so a
 * long hint squeezes the file name to nothing. Fourteen of the lake's
 * documents are fleet-wide and mention twenty-plus machines; those are capped.
 */
const MAX_HINT_MACHINES = 3;

function machineHint(machines: readonly string[]): string {
  if (machines.length <= MAX_HINT_MACHINES) return machines.join(' ');
  const shown = machines.slice(0, MAX_HINT_MACHINES).join(' ');
  return `${shown} +${machines.length - MAX_HINT_MACHINES}`;
}

function toResult(entry: LakeEntry): PaletteResult {
  const machines = entry.machines ?? [];
  return {
    // Keyed by slug, not by file: `LakeIndex.file_collisions` proves file
    // names are not unique and a palette row's id has to be.
    id: entry.slug,
    label: entry.file,
    hint: machines.length ? `${entry.conn} · ${machineHint(machines)}` : entry.conn,
    icon: FileText,
    to: `/integration/data?doc=${encodeURIComponent(entry.file)}`,
  };
}

/** Fetch the catalogue once, then re-register so an open palette refreshes. */
function warm(): void {
  if (started) return;
  started = true;
  loadLakeIndex().then(
    (index) => {
      rows = index.entries.map(toResult);
      haystack = index.entries.map((e) =>
        `${e.file} ${e.conn} ${e.folder} ${(e.machines ?? []).join(' ')}`.toLowerCase(),
      );
      register();
    },
    () => {
      // A failed catalogue leaves the source empty rather than breaking ⌘K.
      started = false;
    },
  );
}

function register(): void {
  registerPaletteSource({
    id: 'documents',
    label: 'Documents',
    // Below the entity sources, above navigation: a file name is a more
    // specific thing to search for than a page, and less specific than a
    // machine id.
    order: 60,
    search: (query) => {
      warm();
      if (rows.length === 0) return [];
      if (!query) return rows.slice(0, 5);
      const out: PaletteResult[] = [];
      for (let i = 0; i < rows.length && out.length < 8; i++) {
        if (haystack[i].includes(query)) out.push(rows[i]);
      }
      return out;
    },
  });
}

register();
